import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { getDomains, getProgress } from '../api';
import { BookOpen, CheckCircle2, Circle, TrendingUp, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';

const TaskListBrowser = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [progress, setProgress] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [domainsData, progressData] = await Promise.all([
        getDomains(),
        getProgress()
      ]);
      
      setDomains(domainsData);
      setProgress(progressData);
      setSelectedDomain(domainsData[0]?.id);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error loading data',
        description: 'Failed to load task list. Please refresh.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getMasteryStatus = (mastery) => {
    if (mastery >= 80) return { label: 'Mastered', color: 'bg-green-500', icon: CheckCircle2 };
    if (mastery >= 40) return { label: 'Learning', color: 'bg-yellow-500', icon: TrendingUp };
    return { label: 'Not Started', color: 'bg-slate-300', icon: Circle };
  };

  const getDifficultyColor = (difficulty) => {
    if (difficulty === 'foundational') return 'bg-blue-100 text-blue-800';
    if (difficulty === 'intermediate') return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentDomain = domains.find(d => d.id === selectedDomain);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">BACB Task List</h1>
            <p className="text-lg text-slate-600 mt-1">Browse and study all task list items</p>
          </div>
          <Button 
            onClick={() => navigate('/')}
            variant="outline"
            className="hover:bg-slate-100 transition-colors duration-200"
          >
            Back to Dashboard
          </Button>
        </div>

        {/* Domain Tabs */}
        <Tabs value={selectedDomain} onValueChange={setSelectedDomain} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto gap-2 bg-white p-2 rounded-lg shadow-sm">
            {domains.map(domain => {
              const domainItems = domain.items;
              const masteredInDomain = domainItems.filter(
                item => progress?.item_progress[item.code]?.mastery >= 80
              ).length;
              const domainProgress = Math.round((masteredInDomain / domainItems.length) * 100);

              return (
                <TabsTrigger 
                  key={domain.id} 
                  value={domain.id}
                  className="flex flex-col items-start p-4 data-[state=active]:bg-slate-100 transition-all duration-200"
                >
                  <div className="flex items-center gap-2 w-full">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: domain.color }}
                    />
                    <span className="font-medium text-sm">{domain.name}</span>
                  </div>
                  <div className="w-full mt-2">
                    <Progress value={domainProgress} className="h-1" />
                    <span className="text-xs text-slate-500 mt-1">{domainProgress}%</span>
                  </div>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {domains.map(domain => (
            <TabsContent key={domain.id} value={domain.id} className="space-y-4">
              {/* Domain Summary Card */}
              <Card className="border-l-4 hover:shadow-lg transition-shadow duration-300" style={{ borderLeftColor: domain.color }}>
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <BookOpen className="h-6 w-6" style={{ color: domain.color }} />
                    {domain.name}
                  </CardTitle>
                  <CardDescription>
                    {domain.items.length} task items in this domain
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Task Items */}
              <div className="grid gap-4">
                {domain.items.map(item => {
                  const itemProgress = progress?.item_progress[item.code];
                  const mastery = itemProgress?.mastery || 0;
                  const status = getMasteryStatus(mastery);
                  const StatusIcon = status.icon;

                  return (
                    <Card 
                      key={item.id} 
                      className="hover:shadow-lg transition-all duration-300 cursor-pointer group"
                      onClick={() => navigate(`/flashcards?task=${item.code}`)}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3">
                              <Badge 
                                variant="outline" 
                                className="text-sm font-bold"
                                style={{ borderColor: domain.color, color: domain.color }}
                              >
                                {item.code}
                              </Badge>
                              <Badge className={getDifficultyColor(item.difficulty)}>
                                {item.difficulty}
                              </Badge>
                              <div className="flex items-center gap-1">
                                <StatusIcon className="h-4 w-4" style={{ color: domain.color }} />
                                <span className="text-sm text-slate-600">{status.label}</span>
                              </div>
                            </div>
                            <CardTitle className="text-base leading-relaxed group-hover:text-blue-600 transition-colors duration-200">
                              {item.description}
                            </CardTitle>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">Mastery Level</span>
                            <span className="font-semibold" style={{ color: domain.color }}>
                              {mastery}%
                            </span>
                          </div>
                          <Progress value={mastery} className="h-2" />
                          {itemProgress && (
                            <div className="flex items-center justify-between text-xs text-slate-500 pt-2">
                              <span>Reviewed {itemProgress.review_count} times</span>
                              <span>
                                Next review: {itemProgress.next_review && new Date(itemProgress.next_review) <= new Date() 
                                  ? 'Due now' 
                                  : itemProgress.next_review ? new Date(itemProgress.next_review).toLocaleDateString() : 'Not scheduled'}
                              </span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Study Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <Button 
                  size="lg" 
                  className="w-full transition-all duration-200"
                  style={{ backgroundColor: domain.color }}
                  onClick={() => navigate(`/flashcards?domain=${domain.id}`)}
                >
                  <BookOpen className="mr-2 h-5 w-5" />
                  Study {domain.name} Flashcards
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full hover:bg-slate-100 transition-all duration-200"
                  onClick={() => navigate(`/quiz?domain=${domain.id}`)}
                >
                  Take {domain.name} Quiz
                </Button>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default TaskListBrowser;